/**
 * Capture guidance (docs/PRODUCT.md §3.1). `autoChecked` records whether the app
 * verifies the row live, which is what the setup checklist renders.
 */
export interface GuideRow {
  id: string;
  title: string;
  summary: string;
  detail: string;
  autoChecked: 'yes' | 'partial' | 'no';
}

export const GUIDE_ROWS: GuideRow[] = [
  {
    id: 'position',
    title: 'Camera at your side',
    // {distance} is filled in the reader's units (lib/units.ts).
    summary: 'Straight at your side, hip-to-chest height, {distance} away.',
    detail:
      'Stand the phone on a tripod or a stack of books so it is level and does not drift. The app checks the side view from the horizontal gap between your shoulders, and uses hip and knee visibility to judge distance.',
    autoChecked: 'yes',
  },
  {
    id: 'frame',
    title: 'Frame head to knees',
    summary: 'Side-on, from above your head down to your knees. Your feet do not need to be in shot.',
    detail: 'Checked live from the nose and knee landmarks.',
    autoChecked: 'yes',
  },
  {
    id: 'hair',
    title: 'Ear must be visible',
    summary: 'Tie long hair back or tuck it behind your ear.',
    detail:
      'The neck angle is measured from the ear. If the ear is hidden the angle cannot be calculated, so the app warns you rather than guessing.',
    autoChecked: 'yes',
  },
  {
    id: 'clothes',
    title: 'Fitted clothing',
    summary: 'Hoodies, baggy knits and padded jackets hide the shoulder and hip.',
    detail: 'Not automatically checked — if tracking looks unstable, this is the first thing to change.',
    autoChecked: 'no',
  },
  {
    id: 'light',
    title: 'Light on you, not behind you',
    summary: 'Do not sit with a window at your back.',
    detail: 'The app measures average frame brightness and warns when it is too dark or too bright.',
    autoChecked: 'yes',
  },
  {
    id: 'chair',
    title: 'Watch the chair back',
    summary: 'A high back or armrest can hide your hip — angle the camera up to 15° forward.',
    detail: 'You get a hip-visibility warning when this happens.',
    autoChecked: 'partial',
  },
  {
    id: 'desk',
    title: 'A desk hiding your waist is fine',
    summary: 'If your knees are fully hidden, knee metrics simply switch off.',
    detail: 'Each metric disables itself when its landmarks are not reliable.',
    autoChecked: 'yes',
  },
  {
    id: 'phone',
    title: 'Plug the phone in',
    summary: 'Charger connected, brightness low, do-not-disturb on.',
    detail:
      'Sessions over an hour warm the phone up. Dim mode and the automatic frame-rate drop keep this manageable.',
    autoChecked: 'no',
  },
  {
    id: 'background',
    title: 'Nobody walking past',
    summary: 'The session pauses if a second person appears. No mirrors behind you.',
    detail: 'The detector looks for up to two people and pauses rather than measuring the wrong one.',
    autoChecked: 'yes',
  },
];

/** A row's summary with its placeholders filled for this reader. */
export function guideSummary(row: GuideRow, distance: string): string {
  return row.summary.replace('{distance}', distance);
}
